/**
 * erpHandoverBody.ts — which sheets the direct handover can send, and the body
 * it posts. No auth, no fetch, no MSAL.
 *
 * SPLIT OUT FROM erpHandoverClient FOR THE SAME REASON erpTableUrl IS SPLIT
 * OUT FROM erpClient: the client imports the token helper, which imports
 * msalConfig, which THROWS on a missing env var at module load. A test that
 * only wants to know which tables are sendable would have to stand up the
 * whole auth configuration to ask.
 */

import type { IErpSheet } from "./fulfillmentExport";

/**
 * The endpoint's key for a sheet, or null when it cannot be sent.
 *
 * Keyed on the ERP import path rather than the stage, because BQC5 is TWO
 * sheets — QC Passed and QC Failed — and the stage alone cannot tell them
 * apart.
 *
 * "/manufacturing_order" returning null is the whole point: the zip has BQC1
 * unticked by DEFAULT, which a person can change, while here there is nothing
 * to change. The fulfillment team raises those orders (Mikko, 2026-09-22).
 */
export function handoverKeyFor(sheet: IErpSheet): string | null {
  switch (sheet.importPath) {
    case "/cutting": return "cutting";
    case "/assigning": return "assigning";
    case "/receiving": return "receiving";
    case "/qc_passed": return "qc_passed";
    case "/qc_failed": return "qc_failed";
    default: return null;
  }
}

/** The sheets this route can carry, in the order they are applied. */
export function sendableSheets(sheets: readonly IErpSheet[]): IErpSheet[] {
  return sheets.filter((s) => handoverKeyFor(s) !== null);
}

export interface IHandoverBody {
  dryRun: boolean;
  tables: Record<string, { rows: Record<string, string>[] }>;
}

/**
 * `dryRun` is always stated, never omitted. The endpoint treats anything that
 * is not an explicit `false` as a preview, so an omitted flag would be safe —
 * but a body that says what it means is one fewer thing to reason about at the
 * only place in this app that writes somebody else's production database.
 */
export function buildHandoverBody(
  sheets: readonly IErpSheet[], dryRun: boolean
): IHandoverBody {
  const tables: IHandoverBody["tables"] = {};
  for (const sheet of sheets) {
    const key = handoverKeyFor(sheet);
    if (!key) continue;
    tables[key] = { rows: sheet.rows };
  }
  return { dryRun, tables };
}

/**
 * The gap: a table a Send would actually do SOMETHING to, per sheet.
 *
 * Built from a CHECK, which is keys-only and cheap. That turns a send of
 * 224,894 rows into a send of however many tables are genuinely worth
 * touching. The scope picker then stops mattering for cost, which is the
 * point: idempotency already made it not matter for correctness.
 *
 * BOTH `inserted` AND `filled` gate this, not `inserted` alone — a table with
 * nothing new to INSERT can still have already-there rows with blank cells
 * worth FILLING, and skipping it would make the fill-blanks half of the write
 * unreachable for every table that never has a genuinely new WIP again.
 */
export function gapSheets(
  sheets: readonly IErpSheet[], checked: IHandoverResult
): IErpSheet[] {
  const byTable = new Map(checked.tables.map((t) => [t.importPath, t]));
  return sendableSheets(sheets).map((sheet) => {
    const seen = byTable.get(sheet.importPath);
    // No line in the report for this table means the check never reached it —
    // send it whole rather than silently sending nothing.
    if (!seen) return sheet;
    // Nothing to add or fill: an empty sheet still goes, so the result names
    // the table and says 0 rather than the table vanishing from the report.
    return { ...sheet, rows: seen.inserted === 0 && seen.filled === 0 ? [] : sheet.rows };
  });
}

export interface IHandoverTableResult {
  table: string;
  importPath: string;
  received: number;
  /** On a dry run, what WOULD be inserted. */
  inserted: number;
  /**
   * Rows whose WIP the table already held, that had a chance to gain a value
   * they were missing. On a dry run this is the upper bound (every
   * already-there row); on a write it is how many the UPDATE actually
   * matched, which can never exceed `alreadyThere`.
   */
  filled: number;
  alreadyThere: number;
  noWip: number;
  duplicateInPayload: number;
  /**
   * Order Monitoring rows this table's import ALSO updates. Their own
   * importer does it — `sync_monitoring_from_cutting` and friends — so the
   * direct route does too, and it is reported separately because it is a
   * write to a different table from the one the rows went into.
   */
  monitoringUpdated: number;
  monitoringColumns: string[];
}

/**
 * Rows per REQUEST. Mirrors ERP_HANDOVER_REQUEST_ROWS on the server.
 *
 * The real handover is 224,894 rows — 60,402 of them cutting — so it cannot
 * be one request whatever the server's cap says: that much JSON is past what
 * the Static Web App's proxy will carry. It goes as a sequence of requests,
 * one table at a time, and IDEMPOTENCY is what makes that safe: a send that
 * stops half way is finished by pressing Send again, because every row
 * already written is skipped.
 */
export const HANDOVER_REQUEST_ROWS = 5_000;

/**
 * Rows per request when CHECKING, which can be far larger — see `wipOnly`.
 *
 * 25,000 WIP numbers is a few hundred kilobytes, so the real handover checks
 * in about nine requests instead of forty-five, and hits their live tables
 * with nine DISTINCT scans instead of forty-five. That matters because the
 * server it runs on is a Burstable B1ms which their own staff are working
 * against at the same time.
 *
 * Must stay under `ERP_HANDOVER_CHECK_MAX_ROWS` in `api/src/shared/
 * erpHandover.ts` (100,000) — the server's SEPARATE, larger cap for a dry
 * run's much lighter payload. The two drifted apart once already: this was
 * 25,000 while the server's ONE cap was still 20,000 (sized for a write), so
 * checking any table over 20,000 rows failed with a 413 that read like a
 * scope problem and was actually a payload-shape one.
 */
export const HANDOVER_CHECK_ROWS = 25_000;

/**
 * A row stripped to its WIP number.
 *
 * `planHandover` on the server reads NOTHING ELSE from a row — not the date,
 * not the qty, not the SKU — and a dry run never reaches the INSERT that
 * would want them. So sending whole rows to be told what would happen pays
 * the entire cost of the write for none of its benefit: about 60 MB up the
 * wire for the real handover.
 *
 * Projecting to the key is therefore not an approximation of the check, it is
 * the same check on the same input, and the test asserts the two plan alike.
 */
export function wipOnly(
  rows: readonly Record<string, string>[]
): Record<string, string>[] {
  return rows.map((r) => ({ "WIP No.": String(r["WIP No."] ?? "") }));
}

/** One request's worth of work: a sheet and the slice of it being sent. */
export interface IHandoverChunk {
  sheet: IErpSheet;
  rows: Record<string, string>[];
  /** 1-based, for the progress line. */
  index: number;
  of: number;
}

/**
 * Split the whole handover into requests, one table at a time and in the
 * order the tables must be applied.
 *
 * A table with NO rows still gets one chunk. Sending it is harmless and its
 * result line then says "0 received" rather than the table vanishing from the
 * report — the same reasoning as the zip writing a header-only sheet.
 */
export function planHandoverRequests(
  sheets: readonly IErpSheet[], size: number = HANDOVER_REQUEST_ROWS
): IHandoverChunk[] {
  const out: IHandoverChunk[] = [];
  for (const sheet of sendableSheets(sheets)) {
    if (sheet.rows.length === 0) {
      out.push({ sheet, rows: [], index: 1, of: 1 });
      continue;
    }
    const parts: Record<string, string>[][] = [];
    for (let i = 0; i < sheet.rows.length; i += size) {
      parts.push(sheet.rows.slice(i, i + size));
    }
    parts.forEach((rows, i) => out.push({ sheet, rows, index: i + 1, of: parts.length }));
  }
  return out;
}

/**
 * One chunk's body: a single table, so a request is never mixed.
 *
 * On a DRY RUN the rows are projected to their WIP numbers alone, because
 * that is all the server's plan reads. On a write they go whole.
 */
export function buildChunkBody(chunk: IHandoverChunk, dryRun: boolean): IHandoverBody {
  const key = handoverKeyFor(chunk.sheet);
  if (!key) return { dryRun, tables: {} };
  return { dryRun, tables: { [key]: { rows: dryRun ? wipOnly(chunk.rows) : chunk.rows } } };
}

/** Add one chunk's per-table figures into a running total. */
export function mergeResults(
  into: IHandoverResult | null, add: IHandoverResult
): IHandoverResult {
  if (!into) return add;
  const tables = [...into.tables];
  for (const t of add.tables) {
    const seen = tables.findIndex((x) => x.table === t.table);
    if (seen < 0) { tables.push({ ...t }); continue; }
    const prev = tables[seen];
    tables[seen] = {
      ...prev,
      received: prev.received + t.received,
      inserted: prev.inserted + t.inserted,
      filled: prev.filled + t.filled,
      alreadyThere: prev.alreadyThere + t.alreadyThere,
      noWip: prev.noWip + t.noWip,
      duplicateInPayload: prev.duplicateInPayload + t.duplicateInPayload,
      monitoringUpdated: prev.monitoringUpdated + t.monitoringUpdated,
    };
  }
  const sum = (k: keyof IHandoverResult["totals"]) =>
    tables.reduce((a, t) => a + (t[k as keyof IHandoverTableResult] as number), 0);
  return {
    dryRun: into.dryRun && add.dryRun,
    // ANY chunk that wrote makes this a write. A run where the setting was
    // turned on half way through must not report itself as a preview.
    wrote: into.wrote || add.wrote,
    at: add.at,
    totals: {
      received: sum("received"), inserted: sum("inserted"), filled: sum("filled"),
      alreadyThere: sum("alreadyThere"), noWip: sum("noWip"),
      duplicateInPayload: sum("duplicateInPayload"),
      monitoringUpdated: sum("monitoringUpdated"),
    },
    tables,
  };
}

export interface IHandoverResult {
  dryRun: boolean;
  /**
   * The SERVER saying whether it actually wrote — never inferred from what was
   * requested. A write refused because ERP_HANDOVER_ENABLED is unset comes
   * back as a dry run, and must not read as done.
   */
  wrote: boolean;
  at: string;
  totals: {
    received: number; inserted: number; filled: number; alreadyThere: number;
    noWip: number; duplicateInPayload: number; monitoringUpdated: number;
  };
  tables: IHandoverTableResult[];
}
